import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAnnotations } from '../../contexts/AnnotationContext';
import { useBible } from '../../contexts/BibleContext';
import { getVerse as getLocalVerse } from '../../services/localBibleApi';
import { getBibleApiVerse } from '../../services/bibleApi';
import { getBibleShareUrl } from '../../constants/bibleConstants';
import { shareContent } from '../../utils/nativeBridge';
import Icon from '../UI/Icon';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import './AnnotationPanel.css';

function AnnotationPanel({ verseId, bibleId, verseNum, initialTab = 'highlight', onClose, onStartRangeSelection }) {
  const {
    highlightColors,
    addHighlight,
    removeHighlight,
    getHighlightsForVerse,
    addNote,
    deleteNote,
    getNotesForVerse,
    addBookmark,
    removeBookmark,
    isBookmarked
  } = useAnnotations();
  
  const { availableBibles } = useBible();
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [noteContent, setNoteContent] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [comparisons, setComparisons] = useState([]);
  const [loadingComparisons, setLoadingComparisons] = useState(false);
  
  // Enable swipe-to-close on mobile
  const { ref: swipeRef } = useSwipeToClose(onClose);
  
  // Sync activeTab with initialTab prop when it changes
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  
  const highlights = getHighlightsForVerse(verseId, bibleId);
  const notes = getNotesForVerse(verseId, bibleId);
  const bookmarked = isBookmarked(verseId, bibleId);
  const currentHighlight = highlights[0];
  
  // Handle highlight
  const handleHighlight = useCallback(async (color) => {
    if (currentHighlight) {
      await removeHighlight(currentHighlight.id);
    }
    if (color) {
      await addHighlight(verseId, bibleId, color);
    }
  }, [verseId, bibleId, currentHighlight, addHighlight, removeHighlight]);
  
  // Handle note save
  const handleSaveNote = useCallback(async () => {
    if (noteContent.trim()) {
      await addNote(verseId, bibleId, noteContent.trim());
      setNoteContent('');
    }
  }, [verseId, bibleId, noteContent, addNote]);
  
  // Handle bookmark toggle
  const handleToggleBookmark = useCallback(async () => {
    if (bookmarked) {
      await removeBookmark(verseId, bibleId);
    } else {
      await addBookmark(verseId, bibleId);
    }
  }, [verseId, bibleId, bookmarked, addBookmark, removeBookmark]);
  
  // Voice recording (Web Speech API)
  const handleVoiceRecord = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Voice recognition is not supported in this browser.');
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    
    recognition.onstart = () => setIsRecording(true);
    recognition.onend = () => setIsRecording(false);
    
    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setNoteContent(prev => prev + ' ' + transcript);
    };
    
    if (isRecording) {
      recognition.stop();
    } else {
      recognition.start();
    }
  }, [isRecording]);
  
  // Copy verse with shareable link
  const handleCopy = useCallback(async () => {
    try {
      // Parse verseId like "Gen.1.1"
      const [bookId, chapter, verse] = verseId.split('.');
      
      // Get verse data
      const isBibleApiTranslation = bibleId && bibleId.startsWith('bible_api_');
      let verseData;
      
      if (isBibleApiTranslation) {
        verseData = await getBibleApiVerse(bibleId, bookId, parseInt(chapter), parseInt(verse));
      } else {
        verseData = await getLocalVerse(bibleId, bookId, parseInt(chapter), parseInt(verse));
      }
      
      if (verseData && verseData.text) {
        // Get translation info
        const translation = availableBibles.find(b => b.id === bibleId);
        const translationName = translation?.abbreviation || translation?.name || bibleId;
        
        // Get book name for display - extract everything before the chapter:verse pattern
        // e.g., "2 Timothy 1:7" -> "2 Timothy", "Genesis 1:1" -> "Genesis"
        const refMatch = verseData.reference?.match(/^(.+?)\s+\d+:\d+/);
        const bookName = refMatch ? refMatch[1] : bookId;
        
        const shareUrl = getBibleShareUrl(bookId, chapter, verse);
        
        // Format: "Book Chapter:Verse - Version\nVerse text\nLink"
        const reference = `${bookName} ${chapter}:${verse}`;
        const formattedText = `${reference} - ${translationName}\n${verseData.text}\n${shareUrl}`;
        
        await navigator.clipboard.writeText(formattedText);
      } else {
        // Fallback to just the reference if verse text not available
        const text = `${verseId} - Verse text not available`;
        await navigator.clipboard.writeText(text);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback to just the reference
      const text = `${verseId} - Verse text not available`;
      await navigator.clipboard.writeText(text);
    }
  }, [verseId, bibleId, availableBibles]);

  const handleShare = useCallback(async () => {
    try {
      const [bookId, chapter, verse] = verseId.split('.');
      const isBibleApiTranslation = bibleId && bibleId.startsWith('bible_api_');
      let verseData;

      if (isBibleApiTranslation) {
        verseData = await getBibleApiVerse(bibleId, bookId, parseInt(chapter), parseInt(verse));
      } else {
        verseData = await getLocalVerse(bibleId, bookId, parseInt(chapter), parseInt(verse));
      }

      if (!verseData?.text) return;

      const translation = availableBibles.find(b => b.id === bibleId);
      const translationName = translation?.abbreviation || translation?.name || bibleId;
      const refMatch = verseData.reference?.match(/^(.+?)\s+\d+:\d+/);
      const bookName = refMatch ? refMatch[1] : bookId;
      const reference = `${bookName} ${chapter}:${verse}`;
      const shareUrl = getBibleShareUrl(bookId, chapter, verse);
      const formattedText = `${reference} - ${translationName}\n${verseData.text}`;

      const shared = await shareContent({
        title: reference,
        text: formattedText,
        url: shareUrl,
      });

      if (!shared) {
        await navigator.clipboard.writeText(`${formattedText}\n${shareUrl}`);
      }
    } catch (err) {
      console.error('Failed to share:', err);
    }
  }, [verseId, bibleId, availableBibles]);
  
  // Load comparisons when compare tab is activated
  const loadComparisons = useCallback(async () => {
    if (!verseId) return;
    
    setLoadingComparisons(true);
    try {
      // Parse verseId like "Gen.1.1"
      const [bookId, chapter, verse] = verseId.split('.');
      
      const comparisonPromises = availableBibles.map(async (bible) => {
        try {
          const isBibleApiTranslation = bible.id && bible.id.startsWith('bible_api_');
          
          let verseData;
          if (isBibleApiTranslation) {
            verseData = await getBibleApiVerse(bible.id, bookId, parseInt(chapter), parseInt(verse));
          } else {
            verseData = await getLocalVerse(bible.id, bookId, parseInt(chapter), parseInt(verse));
          }
          
          return {
            translationId: bible.id,
            translationName: bible.name,
            translationAbbr: bible.abbreviation || bible.id,
            text: verseData?.text || 'Not available',
            available: !!verseData
          };
        } catch (err) {
          return {
            translationId: bible.id,
            translationName: bible.name,
            translationAbbr: bible.abbreviation || bible.id,
            text: 'Error loading',
            available: false
          };
        }
      });
      
      const results = await Promise.all(comparisonPromises);
      setComparisons(results);
    } catch (err) {
      console.error('Error loading comparisons:', err);
    } finally {
      setLoadingComparisons(false);
    }
  }, [verseId, availableBibles]);
  
  // Load comparisons when compare tab is opened
  React.useEffect(() => {
    if (activeTab === 'compare' && comparisons.length === 0) {
      loadComparisons();
    }
  }, [activeTab, comparisons.length, loadComparisons]);
  
  return (
    <div className="annotation-panel-overlay" onClick={onClose}>
      <div className="annotation-panel" ref={swipeRef} onClick={e => e.stopPropagation()}>
        <div className="modal-drag-handle" />
        <div className="panel-header">
          <div className="panel-header-left">
            <div className="verse-reference">
              <Icon name="book" size={18} />
              <span>{verseId}</span>
            </div>
          </div>
          
          <div className="panel-header-center">
            <button 
              className="btn btn-ghost copy-btn" 
              onClick={handleCopy}
              title="Copy verse"
            >
              <Icon name="copy" size={24} />
              <span>Copy</span>
            </button>
            {onStartRangeSelection && (
              <button 
                className="btn btn-ghost copy-btn" 
                onClick={onStartRangeSelection}
                title="Select multiple verses"
              >
                <Icon name="highlight" size={24} />
                <span>Select Range</span>
              </button>
            )}
          </div>
          
          <div className="panel-header-right">
            <button className="btn btn-ghost btn-icon" onClick={onClose}>
              <Icon name="close" size={20} />
            </button>
          </div>
        </div>
        
        <div className="panel-tabs">
          <button 
            className={`tab ${activeTab === 'highlight' ? 'active' : ''}`}
            onClick={() => setActiveTab('highlight')}
          >
            <Icon name="highlight" size={16} />
            Highlight
          </button>
          <button 
            className={`tab ${activeTab === 'note' ? 'active' : ''}`}
            onClick={() => setActiveTab('note')}
          >
            <Icon name="note" size={16} />
            Note
          </button>
          <button 
            className={`tab ${activeTab === 'bookmark' ? 'active' : ''}`}
            onClick={() => setActiveTab('bookmark')}
          >
            <Icon name={bookmarked ? 'bookmarkFilled' : 'bookmark'} size={16} />
            Save
          </button>
          <button 
            className={`tab ${activeTab === 'compare' ? 'active' : ''}`}
            onClick={() => setActiveTab('compare')}
          >
            <Icon name="compare" size={16} />
            Compare
          </button>
        </div>
        
        <div className="panel-content">
          {activeTab === 'highlight' && (
            <div className="highlight-section">
              <p className="section-description">
                Choose a highlight color for this verse
              </p>
              <p className="privacy-hint">
                <Icon name="info" size={12} />
                Stored locally on your device only
              </p>
              <div className="highlight-options">
                {Object.entries(highlightColors).map(([name, color]) => (
                  <button
                    key={name}
                    className={`highlight-option ${currentHighlight?.color === name ? 'selected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => handleHighlight(name)}
                  >
                    {currentHighlight?.color === name && (
                      <Icon name="check" size={16} />
                    )}
                  </button>
                ))}
                <button
                  className={`highlight-option remove ${!currentHighlight ? 'selected' : ''}`}
                  onClick={() => handleHighlight(null)}
                >
                  <Icon name="x" size={16} />
                </button>
              </div>
            </div>
          )}
          
          {activeTab === 'note' && (
            <div className="note-section">
              <p className="privacy-hint">
                <Icon name="info" size={12} />
                Notes are stored locally—TruthBound never sees them. <Link to="/settings">Backup in Settings</Link>.
              </p>
              <div className="note-input-container">
                <textarea
                  className="note-input"
                  placeholder="Write your thoughts about this verse..."
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  rows={4}
                />
                <div className="note-actions">
                  <button 
                    className={`btn btn-ghost btn-icon ${isRecording ? 'recording' : ''}`}
                    onClick={handleVoiceRecord}
                    title="Voice to text"
                  >
                    <Icon name="mic" size={18} />
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleSaveNote}
                    disabled={!noteContent.trim()}
                  >
                    Save Note
                  </button>
                </div>
              </div>
              
              {notes.length > 0 && (
                <div className="existing-notes">
                  <h4 className="notes-title">Your Notes</h4>
                  {notes.map(note => (
                    <div key={note.id} className="note-card">
                      <p className="note-text">{note.content}</p>
                      <div className="note-meta">
                        <span className="note-date">
                          {new Date(note.createdAt).toLocaleDateString()}
                        </span>
                        <button 
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => deleteNote(note.id)}
                        >
                          <Icon name="trash" size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'bookmark' && (
            <div className="bookmark-section">
              <button 
                className={`bookmark-toggle ${bookmarked ? 'bookmarked' : ''}`}
                onClick={handleToggleBookmark}
              >
                <Icon 
                  name={bookmarked ? 'bookmarkFilled' : 'bookmark'} 
                  size={32} 
                />
                <span>
                  {bookmarked ? 'Saved to Bookmarks' : 'Save to Bookmarks'}
                </span>
              </button>
              
              <div className="quick-actions">
                <button className="btn btn-ghost" onClick={handleCopy}>
                  <Icon name="copy" size={16} />
                  Copy Verse
                </button>
                <button className="btn btn-ghost" onClick={handleShare}>
                  <Icon name="share" size={16} />
                  Share
                </button>
              </div>
            </div>
          )}
          
          {activeTab === 'compare' && (
            <div className="compare-section">
              <p className="section-description">
                View this verse across all available translations
              </p>
              {loadingComparisons ? (
                <div className="loading-comparisons">
                  <p>Loading translations...</p>
                </div>
              ) : (
                <div className="comparison-list">
                  {comparisons.map((comp) => (
                    <div 
                      key={comp.translationId} 
                      className={`comparison-item ${!comp.available ? 'unavailable' : ''}`}
                    >
                      <div className="comparison-header">
                        <span className="translation-abbr">{comp.translationAbbr}</span>
                        <span className="translation-name">{comp.translationName}</span>
                      </div>
                      <p className="comparison-text">{comp.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AnnotationPanel;
